/**
 * The sample packs the four server-owner tools teach themselves with.
 *
 * CFG Builder, Spawn Index, Clash Finder and Stream Budget all do nothing until
 * a `resources/` folder lands on them, and a new user does not have a broken one
 * to hand. A tour of an empty form teaches the chrome; the product is the
 * findings list, and it does not exist until input does. So the tutorial for
 * these four is a built-in pack that produces real findings — not a screenshot
 * that goes stale the next time a client is edited, and not a canned report.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A PRODUCTION MODULE AND NOT AN IMPORT OF `fixtures.ts`
 *
 * The packs here were `fixtures.ts`'s, and the twelve test files that use them
 * still reach them through that path — it re-exports every name this module
 * moved out of it. The move rather than a copy, because a copy is two
 * descriptions of one pack and the one that drifts is the one with no test. The
 * move rather than importing `fixtures.ts` from a client, because that file is
 * 48 KB of `GtaResourceInput` builders, ambulance metas and vanilla samples, and
 * pulling all of it into four pages whose entire pitch is a fast static free
 * tool is a cost paid by every visitor who never presses the button. The
 * dependency runs prod ← test, which is the safe direction.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HONESTY RULES, WHICH ARE THE POINT OF THE FEATURE
 *
 * A demo is input the user did not supply. Every rule below exists because the
 * failure mode is a page that looks like a report about the reader's server.
 *
 *  1. `DEMO_INPUT_NOTE` rides the same notice set the scope notes travel in, so
 *     it appears on screen *and* inside every exported report and generated
 *     config, with no per-export change. A sentence behind a closed disclosure
 *     has not been told, so it is a notice and never a tooltip.
 *  2. `demoDownloadName()` prefixes every download, so a file that leaves the
 *     tab carries the fact in its own name.
 *  3. The demo buttons belong in the empty state only. A demo must never replace
 *     input the user loaded, and "the buttons are gone once anything is loaded"
 *     is the version of that rule with no hidden state and no confirm dialog.
 *  4. Nothing here fabricates a result. Every pack is fed through the same
 *     `intake.ts` entry point a user's own drop takes, so the findings are
 *     produced by the real pipeline or they are not produced at all.
 *  5. `DemoPackUse.proves` names the finding codes each pack must still produce,
 *     and `demoPacks.test.ts` asserts them through the real chain. A demo that
 *     quietly goes clean after a rule change is a public page confidently
 *     reporting no problems in a pack built to have them — the worst failure
 *     this feature can have, and the likeliest way it rots.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BINARY PROBLEM, AND THE TWO GESTURES
 *
 * The fixtures use the string `"binary-stand-in"` for `.yft`/`.ytd`. That is
 * fine for a tool that reads `.meta` text and wrong for the two that read bytes
 * or sizes: a 15-byte `.yft` makes Stream Budget report ~0 MB, which reads as a
 * clean budget — the confident wrong answer the house rule forbids. So the
 * stand-ins here are filled arrays of the length the asset claims, and which
 * gesture a pack is fed through is a property of the tool, not a preference:
 *
 *  - **`"archives"`** — `fromArchives(await pack.archives())`. `intake.ts` takes
 *    a zip entry's size from the central directory and never inflates a binary
 *    entry, so a 5 MB filled `.ytd` costs a few hundred bytes of zip and still
 *    reports as exactly 5 MB. Clash Finder earns `identical` from its own hash
 *    pass, which goes back into the archive for the contested bytes, so the
 *    verdict is honestly earned. This is the gesture for three of the four.
 *  - **`"pick"`** — `fromDirectoryPick(pack.pick())`. Stream Budget's duplicate
 *    pass signs `SIGNATURE_PREFIX_BYTES` off a `File` handle, and on the archive
 *    path there is no handle to sign: intake never inflated the binary entry, so
 *    the body is empty, and `readSignatures` layers only the gesture's own
 *    handles, which an archive pick does not have. Measured, not assumed — a zip
 *    demo on Stream Budget reports its sizes correctly and says, truthfully,
 *    that duplicate detection did not run. `budget.duplicate` is only reachable
 *    through a folder pick, so that is the gesture registered for it.
 *
 * **The bound on the filled stand-ins, stated plainly:** they are valid only for
 * tools that read paths, declared sizes and raw bytes. Do not reuse them for
 * anything that parses an asset's internals — the YTD/YFT readers, the 3D
 * viewer, Paint Booth — because a run of one repeated byte is not a file those
 * can open, and a demo that crashes a parser teaches nothing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT A CLIENT HAS TO DO WITH THIS
 *
 * The four islands are not edited here — this module is the library half, and
 * the buttons land separately. What a client owes a demo, in order:
 *
 *  1. Render `demoPacksFor(tool)` in the **empty state only**, one button per
 *     entry, labelled with `use.teaches` rather than with "Load demo".
 *  2. On click, feed the pack through the gesture `use.gesture` names —
 *     `fromArchives(await pack.archives())` or
 *     `fromDirectoryPick(pack.pick())` — through the same `begin`/`runIntake`
 *     wrapper a real drop goes through. Stream Budget must also have its
 *     compare pass on, or its demo reports sizes and no duplicate; the pick it
 *     is handed is the source of the `held` handles that pass needs.
 *  3. Remember that the result came from a demo, and add `DEMO_INPUT_NOTE` to
 *     the notice set the scope note already travels in. That one line is what
 *     puts the sentence on screen *and* in every export.
 *  4. Pass every download through `demoDownloadName()` while that flag is set,
 *     and badge the result with `DEMO_STATE_LABEL`.
 *  5. Clear the flag when real input arrives, which is the same moment the
 *     buttons stop being rendered.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WEIGHT
 *
 * `zipSync` is behind a dynamic `import("fflate")` inside `archives()`. The
 * *read* half of fflate is already in every one of these pages through
 * `intake.ts`; the write half is not, and a visitor who never presses the button
 * should not download a deflate implementation. `pick()` needs no fflate at all.
 * Materialising a collision pack allocates roughly 14 MB of filled arrays for as
 * long as the drop is held, which is the price of sizes that are real.
 */

// ─── Manifests and metas the packs are built from ────────────────────────────

/** One pack's worth of files, keyed by path relative to the pack's parent. */
export type PackFiles = Record<string, string>;

/**
 * The same, with filled binaries allowed.
 *
 * Separate from `PackFiles` because the twelve test files that import that type
 * hand it to helpers typed on `string`, and widening it would push a
 * `string | Uint8Array` through every one of them for the sake of two packs.
 */
export type DemoPackFiles = Record<string, string | Uint8Array>;

export const CLEAN_MANIFEST = `fx_version 'cerulean'
game 'gta5'

author 'The Workshop'
description 'A clean vehicle resource'
version '1.0.0'

files {
  'vehicles.meta',
  'carvariations.meta',
  'carcols.meta',
  'handling.meta',
}

data_file 'VEHICLE_METADATA_FILE' 'vehicles.meta'
data_file 'VEHICLE_VARIATION_FILE' 'carvariations.meta'
data_file 'CARCOLS_FILE' 'carcols.meta'
data_file 'HANDLING_FILE' 'handling.meta'

client_script 'vehicle_names.lua'
`;

/**
 * A manifest for a resource that ships nothing but a `stream/` folder.
 *
 * FiveM streams every file under a folder called `stream` without being told to,
 * so a parts pack declares nothing and this declares nothing. That is why it
 * exists rather than the collision pair reusing `CLEAN_MANIFEST`: the clean
 * manifest names four metas and a script, none of which a parts pack ships, so a
 * pair built on it answers with a handful of `manifest.path.missing` errors that
 * have nothing to do with the one thing the pair is about.
 */
export const STREAM_ONLY_MANIFEST = `fx_version 'cerulean'
game 'gta5'

author 'The Workshop'
description 'A shared parts pack'
`;

/**
 * The one name both halves of the collision pair ship.
 *
 * Deliberately not a stock name. `police.yft` would make every finding about
 * this pair carry the vanilla-override argument as well, and whether the base
 * game owns the name is a different axis from whether the two copies match.
 */
export const COLLISION_ASSET = "ws_wheel_hre.ytd";

/** A `vehicles.meta` whose only interesting content is its spawn code. */
export function vehiclesMeta(spawnCode: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfo__InitDataList>
  <InitDatas>
    <Item>
      <modelName>${spawnCode}</modelName>
      <txdName>${spawnCode}</txdName>
      <handlingId>${spawnCode}</handlingId>
    </Item>
  </InitDatas>
</CVehicleModelInfo__InitDataList>
`;
}

/**
 * `two-packs-same-layout/` — two vendor packs whose internal paths are
 * character-for-character identical and whose spawn codes are not.
 *
 * This is the shape that a flat-concatenate-then-dedupe intake destroys: one of
 * the two `police/stream/police.yft` entries disappears, which is the collision
 * the tool was opened to find.
 *
 * Serves intake (two origins stay apart), Clash Finder (both packs present,
 * collisions found) and Spawn Index (two codes, not one).
 */
export function twoPacksSameLayout(): { vendor: string; files: PackFiles }[] {
  const layout = (spawnCode: string): PackFiles => ({
    "police/fxmanifest.lua": CLEAN_MANIFEST,
    "police/vehicles.meta": vehiclesMeta(spawnCode),
    "police/stream/police.yft": "binary-stand-in",
    "police/stream/police.ytd": "binary-stand-in",
  });
  return [
    { vendor: "vendor-a", files: layout("copcar_a") },
    { vendor: "vendor-b", files: layout("copcar_b") },
  ];
}

/**
 * `pack-no-manifest/` — two stream-only packs and nothing that declares them.
 *
 * A half-extracted download looks exactly like this, and it is the input for
 * which a naive intake invents a single synthetic root out of the first path's
 * first segment.
 *
 * Serves intake (one resource per stream folder, not one synthetic root), Spawn
 * Index (a code guessed from a stream file name, with no manifest to confirm it)
 * and CFG Builder (an `ensure` block for a folder nothing declares).
 */
export function packNoManifest(): PackFiles {
  return {
    "pack-no-manifest/police/stream/police.yft": "binary-stand-in",
    "pack-no-manifest/police/stream/police.ytd": "binary-stand-in",
    "pack-no-manifest/ambulance/stream/ambulance.yft": "binary-stand-in",
    "pack-no-manifest/ambulance/stream/ambulance.ytd": "binary-stand-in",
    "pack-no-manifest/readme.txt": "extract these into resources/",
  };
}

/**
 * A `server.cfg` that somebody has been maintaining for years.
 *
 * Every awkward thing a real one has, and each is here for a reason a test
 * names:
 *
 *   - a `#` inside a quoted hostname, which a comment stripper that is not
 *     quote-aware cuts the line at;
 *   - two `exec` lines, one with a trailing comment, which a merge must return
 *     byte for byte — they are the user's own files and nothing here can read
 *     them;
 *   - a duplicated `ensure`, so the start order and the set of started
 *     resources are different lengths;
 *   - `ensure` lines in the order that does not boot, matching the
 *     `cfg-wrong-order` pack below;
 *   - `sv_licenseKey changeme`, the placeholder from the FiveM template;
 *   - `sv_maxclients 64` with `set onesync off`, which the server silently runs
 *     at 32.
 *
 * Serves CFG Builder (import mode, and the paste demo) and Clash Finder, which
 * reads `order` out of a file like this to say which copy of a colliding asset
 * is winning today.
 */
export const EXISTING_SERVER_CFG = `# Bob's server. Set up in 2021, do not reformat.
# The exec lines below are maintained by hand.

sv_hostname "Bob's #1 Server"
sets sv_projectName "Bob's RP"

endpoint_add_tcp "0.0.0.0:30120"
endpoint_add_udp "0.0.0.0:30120"

sv_maxclients 64
set onesync off
sv_licenseKey changeme

exec permissions.cfg
exec vehicles.cfg   # the whitelist, hand-maintained

ensure ox_inventory
ensure es_extended
ensure ox_lib
ensure oxmysql
ensure ox_inventory
`;

// ─── Saying it is a demo ─────────────────────────────────────────────────────

/**
 * The sentence that travels with every demo result, on screen and in exports.
 *
 * Added to the same notice set `CLASH_SCOPE_NOTE` and `SPAWN_INDEX_SCOPE_NOTE`
 * already ride in, which is what makes it appear inside a downloaded report
 * without any export code changing. It names the remedy as well as the fact,
 * because "this is a sample" leaves a reader who wanted a report about their own
 * server with nowhere to go.
 */
export const DEMO_INPUT_NOTE =
  "This report was produced from The Workshop's built-in sample pack, not from your files. " +
  "Nothing on this page describes your server. Load your own resources/ folder — or a zip of " +
  "it — to get a report about it.";

/**
 * The badge a surface puts on a demo result, so the client does not invent one.
 *
 * Short because it sits beside the result count, and unambiguous because a user
 * who scrolled past the notice still has to be able to tell at a glance that
 * this is not their server.
 */
export const DEMO_STATE_LABEL = "Sample pack — not your server";

/** Prefixed onto every file a demo result exports. */
export const DEMO_FILENAME_PREFIX = "sample-";

/**
 * The name a download takes when the report behind it came from a demo.
 *
 * A demo-derived export must never be mistakable for a real audit, and the
 * notice inside it is only read by somebody who opens it — the file name is read
 * by somebody scrolling a downloads folder six weeks later. Idempotent, so a
 * caller that has already prefixed a name does not produce `sample-sample-`.
 */
export function demoDownloadName(name: string): string {
  return name.startsWith(DEMO_FILENAME_PREFIX) ? name : `${DEMO_FILENAME_PREFIX}${name}`;
}

// ─── Materialising a pack ────────────────────────────────────────────────────

/**
 * A filled stand-in for a binary asset, at the length the asset claims.
 *
 * One repeated byte, because the two things a demo has to be able to prove about
 * two copies of a name — same bytes, different bytes — are decided by a hash
 * over equal lengths, and a length difference would prove "different" without
 * anybody hashing anything. That teaches the weaker half of the rule: sizes
 * alone can prove *different* and can never prove *identical*.
 *
 * See the module header for the bound on where these are valid.
 */
function bin(size: number, fillByte: number): Uint8Array {
  const out = new Uint8Array(size);
  out.fill(fillByte);
  return out;
}

/**
 * One origin's files as a zip, built in the browser from the same source the
 * tests use.
 *
 * Never a `.zip` checked in under `public/`: a committed archive cannot be
 * reviewed in a diff and drifts from the pack with nothing to catch it.
 */
async function zipOf(name: string, files: DemoPackFiles): Promise<File> {
  const { strToU8, zipSync } = await import("fflate");
  const entries: Record<string, Uint8Array> = {};
  for (const [path, content] of Object.entries(files)) {
    entries[path] = typeof content === "string" ? strToU8(content) : content;
  }
  return new File([zipSync(entries)], name, { type: "application/zip" });
}

/**
 * The `FileList` a `webkitdirectory` input produces for these files.
 *
 * `webkitRelativePath` is the whole of it: it carries the folder structure, it
 * is what `fromDirectoryPick` reads, and it is also what a caller's own
 * signature pass keys its file handles on. Assembled by hand because no
 * constructor for `FileList` exists anywhere — the browser only ever hands one
 * out — so this is the same shape the four client test files build.
 */
function pickListOf(files: DemoPackFiles): FileList {
  const list = Object.entries(files).map(([path, content]) => {
    // A `Uint8Array` is a valid `BlobPart` at runtime; the cast is only here
    // because the DOM lib pins `ArrayBufferView<ArrayBuffer>` while a filled
    // stand-in is typed `Uint8Array<ArrayBufferLike>`. Copying it to satisfy
    // the checker would double the megabytes this pack already costs.
    const body = content as unknown as BlobPart;
    const file = new File([body], path.split("/").pop() ?? path);
    Object.defineProperty(file, "webkitRelativePath", { value: path, configurable: true });
    return file;
  });
  return Object.assign(list, {
    item: (index: number) => list[index] ?? null,
  }) as unknown as FileList;
}

// ─── The registry ────────────────────────────────────────────────────────────

/**
 * The tools a demo pack can be offered on: the four free server-owner tools,
 * then the two creator tools that take folders or zips (tutorials Phase 5).
 */
export type DemoTool =
  | "clash-finder"
  | "spawn-index"
  | "stream-budget"
  | "server-cfg"
  | "pack-bench"
  | "code-smith";

/**
 * Which `intake.ts` entry point a pack must be fed through on a given tool.
 *
 * A property of what the tool reads, not a preference — see the module header.
 */
export type DemoGesture = "archives" | "pick";

/** What one pack is for on one tool. */
export type DemoPackUse = {
  /**
   * One sentence naming the finding the user is about to see. Shown on the
   * button, because "Load demo" tells a reader nothing about why they would.
   *
   * It must describe what the pipeline actually produces. A `teaches` string
   * that promises a finding `proves` does not pin is a screenshot with extra
   * steps.
   */
  teaches: string;
  gesture: DemoGesture;
  /**
   * Finding codes this pack must still produce on this tool, asserted by
   * `demoPacks.test.ts` through the real chain.
   *
   * Empty only where the pack's whole teaching is structural — an index with two
   * entries, an `ensure` block in an order the folder was not in — and then the
   * test file has a named case for it instead. The test asserts that the set of
   * empty-`proves` uses and the set of structurally covered uses are the same
   * set, so an empty one cannot be added without a case to go with it.
   */
  proves: readonly string[];
};

export type DemoPack = {
  id: string;
  /** What the user is told they are loading. */
  label: string;
  /** Keyed by tool; the key set is the set of tools that offer this pack. */
  uses: Readonly<Partial<Record<DemoTool, DemoPackUse>>>;
  /** One `File` per origin. Feed to `fromArchives`. */
  archives: () => Promise<File[]>;
  /** Every file, carrying `webkitRelativePath`. Feed to `fromDirectoryPick`. */
  pick: () => FileList;
};

/**
 * The shared part both halves of a collision pair ship, and its length.
 *
 * Above `BUDGET_THRESHOLDS.duplicateWasteWarn` (4 MiB) on purpose, so the
 * duplicate finding the Stream Budget demo exists to show arrives as a warning
 * rather than as an info row a reader scrolls past. Not far above it: every byte
 * here is allocated in the tab for as long as the drop is held.
 */
const SHARED_PART_BYTES = 5_000_000;

/**
 * Each pack's own model, at lengths that differ from each other and from the
 * shared part.
 *
 * They are here so a duplicate finder has something it must *not* flag, and so a
 * reader can tell "the one name that collides" from "every name in the pack".
 */
const PACK_A_MODEL_BYTES = 2_600_000;
const PACK_B_MODEL_BYTES = 1_900_000;

/** The two parts packs, with the far copy's fill byte the only thing in play. */
function wheelPacks(farFillByte: number): { origin: string; files: DemoPackFiles }[] {
  return [
    {
      origin: "wheels-pack-a",
      files: {
        "wheels-pack-a/fxmanifest.lua": STREAM_ONLY_MANIFEST,
        [`wheels-pack-a/stream/${COLLISION_ASSET}`]: bin(SHARED_PART_BYTES, 0),
        "wheels-pack-a/stream/wsatlas.yft": bin(PACK_A_MODEL_BYTES, 0x22),
      },
    },
    {
      origin: "wheels-pack-b",
      files: {
        "wheels-pack-b/fxmanifest.lua": STREAM_ONLY_MANIFEST,
        [`wheels-pack-b/stream/${COLLISION_ASSET}`]: bin(SHARED_PART_BYTES, farFillByte),
        "wheels-pack-b/stream/wsbravo.yft": bin(PACK_B_MODEL_BYTES, 0x33),
      },
    },
  ];
}

/**
 * The four resources whose folder order is the order that does not boot.
 *
 * Rewritten here as files rather than reused from `fixtures.cfgWrongOrder()`,
 * which is a `GtaResourceInput[]` — the *output* of collection, not an input to
 * it. A demo built on that shape would skip intake, which is the layer three of
 * these four tools are mostly about.
 *
 * The dependencies are declared rather than inferred on purpose. An inferred
 * edge is a guess read out of script text and this layer refuses to raise an
 * error on a guess, so a demo built on inference would be teaching the weaker
 * half of the rule.
 */
function cfgWrongOrderFiles(): PackFiles {
  const manifest = (...needs: string[]): string =>
    "fx_version 'cerulean'\ngame 'gta5'\n" +
    (needs.length === 0 ? "" : `dependencies { ${needs.map((n) => `'${n}'`).join(", ")} }\n`);
  // Folder order is deliberately the broken one: every dependent comes first.
  return {
    "cfg-wrong-order/ox_inventory/fxmanifest.lua": manifest("ox_lib", "oxmysql"),
    "cfg-wrong-order/es_extended/fxmanifest.lua": manifest("oxmysql"),
    "cfg-wrong-order/ox_lib/fxmanifest.lua": manifest(),
    "cfg-wrong-order/oxmysql/fxmanifest.lua": manifest(),
  };
}

/**
 * One vehicle folder the way PackBench expects to be handed one: the five metas
 * its generated manifest declares, a model and dictionary of its own, and the
 * wheel dictionary both folders share.
 *
 * Not the collision pair above. That pair is a parts pack with no metas, and
 * PackBench's manifest declares five meta globs whatever it is given, so it
 * answers with ten `manifest.path.missing` warnings about its own manifest —
 * ranked ahead of the duplicate and past the list's eight-row fold. A demo whose
 * lesson is below the fold under a pile of noise it caused teaches the noise.
 */
function vehicleFolder(code: string, modelBytes: number, fillByte: number): DemoPackFiles {
  const root = (tag: string, body = ""): string =>
    `<?xml version="1.0" encoding="UTF-8"?>\n<${tag}>${body}</${tag}>\n`;
  const item = (fields: string): string => `<Item>${fields}</Item>`;
  return {
    [`${code}/fxmanifest.lua`]: STREAM_ONLY_MANIFEST,
    [`${code}/vehicles.meta`]: root(
      "CVehicleModelInfo__InitDataList",
      `<InitDatas>${item(
        `<modelName>${code}</modelName><txdName>${code}</txdName><handlingId>${code}</handlingId>` +
          `<gameName>${code}</gameName><audioNameHash>police</audioNameHash>`,
      )}</InitDatas>`,
    ),
    [`${code}/handling.meta`]: root(
      "CHandlingDataMgr",
      `<HandlingData><Item type="CHandlingData"><handlingName>${code}</handlingName></Item></HandlingData>`,
    ),
    [`${code}/carvariations.meta`]: root(
      "CVehicleModelInfoVariation",
      `<variationData>${item(`<modelName>${code}</modelName>`)}</variationData>`,
    ),
    [`${code}/carcols.meta`]: root("CVehicleModelInfoVarGlobal"),
    [`${code}/vehiclelayouts.meta`]: root("CVehicleMetadataMgr"),
    [`${code}/stream/${code}.yft`]: bin(modelBytes, fillByte),
    [`${code}/stream/${code}.ytd`]: bin(modelBytes + 100_000, fillByte),
    [`${code}/stream/${COLLISION_ASSET}`]: bin(SHARED_PART_BYTES, 0),
  };
}

/** One pack from a list of origins, materialisable either way. */
function packOf(
  id: string,
  label: string,
  uses: Readonly<Partial<Record<DemoTool, DemoPackUse>>>,
  parts: () => { origin: string; files: DemoPackFiles }[],
): DemoPack {
  return {
    id,
    label,
    uses,
    archives: () => Promise.all(parts().map((part) => zipOf(`${part.origin}.zip`, part.files))),
    pick: () => {
      const all: DemoPackFiles = {};
      for (const part of parts()) Object.assign(all, part.files);
      return pickListOf(all);
    },
  };
}

/**
 * Every pack the four tools can offer, in the order a tool lists them.
 *
 * Five packs rather than one per tool, because the packs overlap: the pair that
 * proves Clash Finder's identical-or-different split is the same pair that
 * proves Stream Budget is paying to download one file twice, and a reader who
 * has already loaded it on one tool recognises it on the next.
 */
export const DEMO_PACKS: readonly DemoPack[] = [
  packOf(
    "two-vendors-same-layout",
    "Two vendors' police cars, same file names",
    {
      "clash-finder": {
        teaches:
          "Both vendor packs ship police.yft and police.ytd at the same paths — the collision, " +
          "with the archive each copy came from named.",
        gesture: "archives",
        proves: ["stream.basename.duplicate"],
      },
      "spawn-index": {
        teaches:
          "Two packs both called police, with two different spawn codes — and each one declares " +
          "a model file it does not ship.",
        gesture: "archives",
        proves: ["vehicle.model.file_missing"],
      },
    },
    () =>
      twoPacksSameLayout().map((pack) => ({
        origin: pack.vendor,
        files: pack.files as DemoPackFiles,
      })),
  ),
  packOf(
    "collision-identical",
    "One shared wheel, shipped by two packs, byte for byte",
    {
      "clash-finder": {
        teaches:
          "One streamed name in two packs, hashed and found identical — a collision that costs " +
          "bandwidth and changes nothing in game.",
        gesture: "archives",
        proves: ["stream.basename.duplicate"],
      },
      "stream-budget": {
        teaches: "The same 5 MB wheel downloaded twice by every player who joins.",
        gesture: "pick",
        proves: ["budget.duplicate"],
      },
    },
    () => wheelPacks(0),
  ),
  packOf(
    "collision-different",
    "The same wheel name from two packs, different files",
    {
      "clash-finder": {
        teaches:
          "The same name at the same length, hashed and found different — one copy is shadowed, " +
          "and with no server.cfg supplied nothing knows which.",
        gesture: "archives",
        proves: ["stream.basename.duplicate"],
      },
    },
    () => wheelPacks(0x5a),
  ),
  packOf(
    "pack-no-manifest",
    "A half-extracted download: two packs, no manifest",
    {
      "spawn-index": {
        teaches:
          "Two resources, not one folder — with a spawn code guessed from each stream file name " +
          "and no manifest to confirm either.",
        gesture: "archives",
        proves: [],
      },
      "server-cfg": {
        teaches: "An ensure block for a folder that declares nothing about itself.",
        gesture: "archives",
        proves: [],
      },
    },
    () => [{ origin: "pack-no-manifest", files: packNoManifest() as DemoPackFiles }],
  ),
  packOf(
    "cfg-wrong-order",
    "Four resources whose folder order does not boot",
    {
      "server-cfg": {
        teaches:
          "ox_inventory and es_extended sit above the two resources they need; the generated " +
          "ensure block puts them back — and es_extended has no database line.",
        gesture: "archives",
        proves: ["cfg.db.missing"],
      },
    },
    () => [{ origin: "cfg-wrong-order", files: cfgWrongOrderFiles() as DemoPackFiles }],
  ),
  packOf(
    "two-vehicles-shared-wheel",
    "Two police cars that ship the same wheel dictionary",
    {
      // A pick, because that is PackBench's gesture: its intake is the folder
      // input, not `intake.ts`. The stand-ins are within their bound — its
      // preflight reads paths, sizes and a 4 KB signature prefix, and its export
      // copies bytes without parsing them.
      "pack-bench": {
        teaches:
          "Packed into one resource, the shared wheel streams twice under one name — and the " +
          "5 MB copy the game never uses is still downloaded by every player.",
        gesture: "pick",
        proves: ["stream.path.duplicate", "budget.duplicate"],
      },
    },
    () => [
      { origin: "copcar_a", files: vehicleFolder("copcar_a", 900_000, 0x22) },
      { origin: "copcar_b", files: vehicleFolder("copcar_b", 700_000, 0x33) },
    ],
  ),
  packOf(
    "script-hot-loop",
    "A script resource with a busy loop and an open money event",
    {
      // Code Smith reads Lua and manifests and names assets without opening
      // them, so this pack is text only and no stand-in is involved. It exists
      // because none of the packs above ship a script. `proves` holds
      // `ScanIssue` categories: the scanner has no rule codes, and its
      // `category` union is the closest thing to a contract it exposes.
      "code-smith": {
        teaches:
          "A Wait(0) loop firing a server event every frame, and a money event any client " +
          "can call.",
        gesture: "archives",
        proves: ["performance", "network", "security"],
      },
    },
    () => [
      {
        origin: "script-hot-loop",
        files: {
          "script-hot-loop/fxmanifest.lua":
            "fx_version 'cerulean'\ngame 'gta5'\n\nclient_script 'client.lua'\nserver_script 'server.lua'\n",
          "script-hot-loop/client.lua": `CreateThread(function()
  while true do
    Wait(0)
    local coords = GetEntityCoords(PlayerPedId())
    DrawMarker(1, coords.x, coords.y, coords.z - 1.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 1.5, 1.5, 0.35, 255, 0, 0, 180, false, false, 2, false, nil, nil, false)
    TriggerServerEvent("demo:saveCoords", coords)
  end
end)
`,
          "script-hot-loop/server.lua": `RegisterNetEvent("demo:giveMoney")
AddEventHandler("demo:giveMoney", function(amount)
  exports.inventory:AddMoney(source, amount)
end)
`,
        },
      },
    ],
  ),
];

/**
 * The demo that is a paste rather than a folder.
 *
 * CFG Builder's best feature is that it *patches* a config instead of replacing
 * it, and it is the least discoverable thing in the tool. It also composes with
 * `cfg-wrong-order`: that pack's dependency edges plus this file's `ensure`
 * order are what make `cfg.ensure.order` fire, so "load the sample folder" then
 * "paste the sample config" is one continuous story ending in a diff that
 * reorders the block.
 *
 * `proves` therefore holds what the *pair* produces. `needsPack` says so rather
 * than leaving a caller to discover that the paste alone reports a config with
 * nothing to check its order against.
 */
export const DEMO_CFG_PASTE: {
  id: string;
  label: string;
  teaches: string;
  text: string;
  needsPack: string;
  proves: readonly string[];
} = {
  id: "existing-server-cfg",
  label: "A server.cfg somebody has been maintaining for years",
  teaches:
    "Paste it over the sample folder and the tool patches it: the ensure block reordered, the " +
    "duplicate start line, onesync off under 64 slots, and the template licence key still in place.",
  text: EXISTING_SERVER_CFG,
  needsPack: "cfg-wrong-order",
  proves: [
    "cfg.ensure.order",
    "cfg.ensure.duplicate",
    "cfg.onesync.maxclients",
    "cfg.license.placeholder",
  ],
};

/**
 * The packs one tool offers, in registry order.
 *
 * Derived from `uses` rather than from a per-tool list, so a pack cannot be
 * offered on a tool without naming what it teaches and what it proves there.
 * That is the structural half of honesty rule 5.
 */
export function demoPacksFor(tool: DemoTool): { pack: DemoPack; use: DemoPackUse }[] {
  const out: { pack: DemoPack; use: DemoPackUse }[] = [];
  for (const pack of DEMO_PACKS) {
    const use = pack.uses[tool];
    if (use) out.push({ pack, use });
  }
  return out;
}
